import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch, fetchOrganizations } from "../services/api";
import "./CandidateReport.css";

export default function CandidateReport() {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [payload, setPayload] = useState(null); // normalized render data
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const data = await apiFetch(`/api/reports/${reportId}`);
        const normalized = normalizeFromApi(data);
        if (!cancelled) {
          setPayload(normalized);

          const name =
            normalized?.meta?.organization_name ||
            normalized?.organization_name ||
            null;

          if (name) {
            setOrgName(name);
          } else if (normalized?.meta?.organization_id != null) {
            try {
              const orgs = await fetchOrganizations();
              const match = (Array.isArray(orgs) ? orgs : []).find(
                (o) =>
                  String(o.id ?? o.organization_id) ===
                  String(normalized.meta.organization_id)
              );
              if (!cancelled && (match?.name || match?.organization_name))
                setOrgName(match?.name ?? match?.organization_name);
            } catch {
              /* ignore, keep empty */
            }
          }
        }
      } catch (e) {
        if (!cancelled)
          setErr(typeof e === "string" ? e : e?.message || "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const {
    meta,
    job_req_reference,
    job_title,
    candidate_name,
    overall_score,
    hiring_recommendations,
    personality_profile,
    technical_review,
    soft_skills_review,
    mismatches_flags,
    originality, // { score, verdict, evidence }
  } = useMemo(() => payload || {}, [payload]);

  const decision = String(hiring_recommendations?.decision || "").toUpperCase();
  const isHire = decision === "HIRE";

  // Originality color theme (5-level scale)
  const oTheme = originalityTheme(originality?.score);

  if (loading) {
    return (
      <div className="report-container">
        <p>Loading report…</p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="report-container">
        <h2>Couldn’t load report</h2>
        <p className="text-red-600">{err}</p>
        <button className="btn" onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }
  if (!payload) {
    return (
      <div className="report-container">
        <h2>No Report Data Found</h2>
        <p>Go back to Reports and open a report again.</p>
        <button className="btn" onClick={() => navigate("/reports")}>Back to Reports</button>
      </div>
    );
  }

  return (
    <div className="report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-row" style={{ marginBottom: 8 }}>
          <div>
            <span className="header-label">Req Number:</span>{" "}
            <span className="header-value">{job_req_reference || "—"}</span>
            {job_title ? (
              <div className="subline"><span className="subline-label">Title:</span> {job_title}</div>
            ) : null}
          </div>
          <div>
            <span className="header-label">Candidate Name:</span>{" "}
            <span className="header-value">{candidate_name || meta?.candidate_id || "—"}</span>
          </div>
        </div>

        <div className="header-row" style={{ marginBottom: 4 /* tighter under Hiring Recommendation */ }}>
          <div>
            <span className="header-label">Hiring Score:</span>{" "}
            {/* (1) Color of score matches Hiring Recommendation color */}
            <span className={`hiring-status ${isHire ? "hire" : "nohire"}`} style={{ padding: "0 8px" }}>
              {fmtPct(overall_score)}
            </span>
          </div>
          <div>
            <span className="header-label">Hiring Recommendation:</span>{" "}
            <span className={`hiring-status ${isHire ? "hire" : "nohire"}`}>
              {hiring_recommendations?.decision || "N/A"}
            </span>
          </div>
        </div>

        {/* Originality row directly under Hiring Score */}
        <div className="header-row" style={{ marginTop: 0 }}>
          <div>
            <span className="header-label">Originality:</span>{" "}
            {/* (3a) Text color of originality score matches the 5-level scale */}
            <span className="header-value" style={{ color: oTheme.fg, fontWeight: 700 }}>
              {fmtPct(originality?.score)}
            </span>
          </div>
          <div />
        </div>

        {/* Organization + Report ID (left). Right column: Start/End/Created + icons */}
        <div className="header-row">
          <div>
            <span className="header-label">Organization:</span>{" "}
            <span className="header-value">{orgName || meta?.organization_name || meta?.organization_id || "—"}</span>
            <div style={{ fontSize: "0.9rem", color: "#64748b", marginTop: 4 }}>
              <span className="subline-label">Report ID:</span>{" "}
              <span style={{ fontWeight: 600 }}>{meta?.interview_report_id || reportId}</span>
            </div>
          </div>
          <div>
            {/* Start / End / Created with slightly smaller font */}
            <div style={{ fontSize: "0.9rem", marginBottom: 4 }}>
              <span className="header-label" style={{ fontSize: "0.9rem" }}>Interview Start Time:</span>{" "}
              <span className="header-value" style={{ fontSize: "0.9rem" }}>
                {meta?.interview_start_time ? new Date(meta.interview_start_time).toLocaleString() : "—"}
              </span>
            </div>
            <div style={{ fontSize: "0.9rem", marginBottom: 4 }}>
              <span className="header-label" style={{ fontSize: "0.9rem" }}>Interview End Time:</span>{" "}
              <span className="header-value" style={{ fontSize: "0.9rem" }}>
                {meta?.interview_end_time ? new Date(meta.interview_end_time).toLocaleString() : "—"}
              </span>
            </div>
            <div style={{ fontSize: "0.9rem" }}>
              <span className="header-label" style={{ fontSize: "0.9rem" }}>Report Created:</span>{" "}
              <span className="header-value" style={{ fontSize: "0.9rem" }}>
                {meta?.created_at ? new Date(meta.created_at).toLocaleString() : "—"}
              </span>
            </div>

            {/* Print & PDF icons */}
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {/* Print */}
              <button
                type="button"
                onClick={() => window.print()}
                title="Print"
                aria-label="Print"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 6,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ color: "#334155" }}
                >
                  <path d="M6 9V2h12v7h-2V4H8v5H6zm12 2a4 4 0 0 1 4 4v5h-4v2H6v-2H2v-5a4 4 0 0 1 4-4h12zm2 7v-3a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3h2v-2h12v2h2z" />
                </svg>
              </button>

              {/* Save as PDF (via print dialog) */}
              <button
                type="button"
                onClick={() => window.print()}
                title="Save as PDF"
                aria-label="Save as PDF"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 6,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8l-6-6zM8 12h2a2 2 0 1 0 0-4H8v4zm0-6h4l4 4h-4a4 4 0 0 1-4-4z" />
                  <text x="9" y="19" fontSize="6" fontFamily="Arial" fill="#334155">PDF</text>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Personality */}
      <div className="section">
        <h3>Personality Profile</h3>
        <div>
          <strong>Myers-Briggs:</strong> {personality_profile?.myers_briggs || "N/A"}
        </div>
        {/* compact inter-block spacing */}
        <div style={{ height: 10 }} />
        <div>
          <strong>OCEAN:</strong> {personality_profile?.ocean || "N/A"}
        </div>
      </div>

      {/* Technical Review */}
      <div className="section">
        <h3>Technical Review</h3>
        <table className="banded-table">
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Resume Score</th>
              <th>Interview Score</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(technical_review) && technical_review.length > 0 ? (
              technical_review.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.requirement ?? item.name ?? "—"}</td>
                  <td>{fmt(item.resume_score)}</td>
                  <td>{fmt(item.interview_score)}</td>
                  <td>{item.comments ?? item.comment ?? "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>No technical review data.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Soft Skills */}
      <div className="section">
        <h3>Soft Skills Review</h3>
        <table className="banded-table">
          <thead>
            <tr>
              <th>Skill</th>
              <th>Score</th>
              <th>Feedback</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(soft_skills_review) && soft_skills_review.length > 0 ? (
              soft_skills_review.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.skill ?? item.name ?? "—"}</td>
                  <td>{fmt(item.score)}</td>
                  <td>{item.feedback ?? item.comment ?? "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3}>No soft skills review data.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Originality Section (above Mismatches) */}
      <div
        className="section"
        style={{
          background: oTheme.bg,
          color: oTheme.fg,
          borderLeft: `4px solid ${oTheme.fg}`,
          borderRadius: 8,
          padding: 16,
        }}
      >
        <h3 style={{ marginTop: 0 }}>Originality</h3>

        <div style={{ marginLeft: 12 }}>
          <p>
            <strong>Verdict:</strong>{" "}
            {originality?.verdict || originalityVerdict(originality?.score)}
          </p>

          <div style={{ marginTop: 8 }}>
            <strong>Evidence:</strong>
            {Array.isArray(originality?.evidence) ? (
              <ul style={{ marginTop: 6, paddingInlineStart: 20 }}>
                {originality.evidence.map((item, idx) => (
                  <li key={idx}>{humanize(item)}</li>
                ))}
              </ul>
            ) : originality?.evidence ? (
              <ul style={{ marginTop: 6, paddingInlineStart: 20 }}>
                {String(originality.evidence)
                  .split(/\n|\u2022|-/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((s, idx) => (
                    <li key={idx}>{s}</li>
                  ))}
              </ul>
            ) : (
              <p style={{ marginTop: 6 }}>No originality evidence.</p>
            )}
          </div>
        </div>
      </div>

      {/* Mismatches */}
      <div className="section">
        <h3>Mismatches / Flags</h3>
        <ul className="mismatch-list">
          {Array.isArray(mismatches_flags) && mismatches_flags.length > 0 ? (
            mismatches_flags.map((flag, idx) => <li key={idx}>{humanize(flag)}</li>)
          ) : (
            <li>No mismatches found.</li>
          )}
        </ul>
      </div>

      {/* Hiring Recommendation feedback */}
      <div className="section">
        <h3>Hiring Recommendation Feedback</h3>
        <div
          className={`recommendation-feedback ${isHire ? "hire" : "nohire"}`}
        >
          {hiring_recommendations?.feedback || "No feedback."}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Helpers & Normalizers ---------------- */

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "—";
  return String(v);
}

// Format percentages and append %
function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return String(v);
  return `${Math.round(n)}%`;
}

/**
 * Render a clean, human-friendly line for mismatches/flags or evidence items.
 * Prefers common text fields; falls back to a compact key:value summary.
 */
function humanize(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);

  if (Array.isArray(v)) {
    const parts = v.map(humanize).filter(Boolean);
    return parts.join("; ");
  }

  if (typeof v === "object") {
    const textLike =
      v.text ??
      v.message ??
      v.reason ??
      v.description ??
      v.note ??
      v.title ??
      v.name ??
      null;

    if (textLike) return String(textLike).trim();

    // Compact fallback: key1: val1; key2: val2
    const entries = Object.entries(v)
      .filter(([, val]) => val !== undefined && val !== null)
      .map(([k, val]) => `${k}: ${typeof val === "object" ? JSON.stringify(val) : String(val)}`);
    return entries.length ? entries.join("; ") : "—";
  }

  return String(v);
}

// 5-level originality verdict
function originalityVerdict(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "UNKNOWN";
  if (s <= 29) return "LIKELY COPIED / PLAGIARIZED";
  if (s <= 49) return "HEAVY AI-ASSISTED";
  if (s <= 69) return "MIXED / UNCERTAIN";
  if (s <= 84) return "MOSTLY ORIGINAL (MINOR ASSIST)";
  return "LIKELY ORIGINAL";
}

// Map originality score to a readable color theme (foreground + background)
function originalityTheme(score) {
  const s = Number(score);
  const neutral = { fg: "#334155", bg: "#f8fafc" }; // slate-700 / slate-50
  if (!Number.isFinite(s)) return neutral;

  if (s <= 29) return { fg: "#991b1b", bg: "#fee2e2" };      // red
  if (s <= 49) return { fg: "#9a3412", bg: "#ffedd5" };      // orange
  if (s <= 69) return { fg: "#92400e", bg: "#fef3c7" };      // amber
  if (s <= 84) return { fg: "#065f46", bg: "#d1fae5" };      // emerald
  return { fg: "#166534", bg: "#dcfce7" };                   // green
}

function parseReportJson(maybe) {
  if (!maybe) return null;
  if (typeof maybe === "object") return maybe;
  try {
    return JSON.parse(maybe);
  } catch {
    return { raw: maybe };
  }
}

function normalizeFromApi(data) {
  if (!data || typeof data !== "object") return null;

  // Try to capture start/end times from several common shapes
  const start =
    data.interview_start_time ??
    data.interview_start ??
    data.start_time ??
    (data.interview && (data.interview.start_time || data.interview.interview_start_time)) ??
    null;

  const end =
    data.interview_end_time ??
    data.interview_end ??
    data.end_time ??
    (data.interview && (data.interview.end_time || data.interview.interview_end_time)) ??
    null;

  const meta = {
    interview_report_id: data.interview_report_id ?? data.id ?? null,
    interview_id: data.interview_id ?? null,
    candidate_id: data.candidate_id ?? null,
    job_req_id: data.job_req_id ?? null,
    organization_id: data.organization_id ?? null,
    organization_name: data.organization_name ?? (data.Organization?.organization_name ?? null),
    created_at: data.created_at ?? null,
    interview_start_time: start ?? null,
    interview_end_time: end ?? null,
  };

  const body = parseReportJson(data.report_json) || {};

  return normalizeReport({ ...body, meta });
}

function normalizeReport(src) {
  if (!src || typeof src !== "object") return null;

  const meta =
    src.meta || {
      interview_report_id: src.interview_report_id ?? src.id ?? null,
      interview_id: src.interview_id ?? null,
      candidate_id: src.candidate_id ?? null,
      job_req_id: src.job_req_id ?? null,
      organization_id: src.organization_id ?? null,
      organization_name: src.organization_name ?? null,
      created_at: src.created_at ?? null,
      interview_start_time: src.interview_start_time ?? null,
      interview_end_time: src.interview_end_time ?? null,
    };

  // If start/end times live at top-level body (some payloads), fold them into meta
  if (src.interview_start_time && !meta.interview_start_time) {
    meta.interview_start_time = src.interview_start_time;
  }
  if (src.interview_end_time && !meta.interview_end_time) {
    meta.interview_end_time = src.interview_end_time;
  }

  const job_req_reference =
    src.job_req_reference ||
    src.req_number ||
    src.job_reference ||
    meta.job_req_id ||
    "";

  const job_title =
    src.job_title ||
    (typeof src.job === "object" ? src.job?.title : "") ||
    src.title ||
    "";

  const candidate_name =
    src.candidate_name ||
    src.candidate ||
    (typeof src.candidate_details === "object" ? src.candidate_details?.name : "") ||
    "";

  const overall_score = src.overall_score ?? src.hiring_score ?? src.score ?? null;

  const hiring_recommendations =
    src.hiring_recommendations ||
    src.hiring_recommendation ||
    { decision: src.decision || "N/A", feedback: src.feedback || "" };

  const personality_profile =
    src.personality_profile ||
    src.personality ||
    {
      myers_briggs: src.myers_briggs || "",
      ocean: src.ocean || "",
    };

  const technical_review =
    src.technical_review ||
    src.technical ||
    (Array.isArray(src.requirements) ? src.requirements : []) ||
    [];

  const soft_skills_review =
    src.soft_skills_review ||
    src.soft_skills ||
    (Array.isArray(src.softSkills) ? src.softSkills : []) ||
    [];

  const mismatches_flags =
    src.mismatches_flags ||
    src.flags ||
    src.mismatches ||
    (Array.isArray(src.issues) ? src.issues : []) ||
    [];

  const originality_raw =
    src.answer_originality ||
    src.originality ||
    src.originality_assessment ||
    null;

  const originality =
    originality_raw && typeof originality_raw === "object"
      ? originality_raw
      : { score: null, verdict: "", evidence: "" };

  return {
    meta,
    job_req_reference,
    job_title,
    candidate_name,
    overall_score,
    hiring_recommendations,
    personality_profile,
    technical_review,
    soft_skills_review,
    mismatches_flags,
    originality,
    organization_name: src.organization_name || meta.organization_name || undefined,
  };
}
